import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { getAgeBand, getEarnedAchievements } from '../utils/ageBands';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMap, FiUser, FiStar, FiZap, FiHeart, FiSmile, FiCpu } = FiIcons;

const ALL_GAMES = [
  // Math & Logic
  { id: 'counting', title: 'Counting Safari', icon: '🦁', color: 'from-orange-400 to-orange-500', minAge: 4, maxAge: 5, category: 'math' },
  { id: 'colors', title: 'Color Explorer', icon: '🌈', color: 'from-pink-400 to-purple-500', minAge: 4, maxAge: 6, category: 'science' },
  { id: 'shapes', title: 'Shape Adventure', icon: '🔷', color: 'from-blue-400 to-blue-500', minAge: 4, maxAge: 7, category: 'math' },
  { id: 'cleanup-quest', title: 'Cleanup Quest', icon: '🧹', color: 'from-teal-400 to-green-500', minAge: 4, maxAge: 7, category: 'life' },
  { id: 'reading-challenge', title: 'Reading Quest', icon: '📖', color: 'from-blue-500 to-indigo-600', minAge: 5, maxAge: 12, category: 'language' },
  { id: 'math', title: 'Math Jungle', icon: '🧮', color: 'from-green-400 to-green-500', minAge: 6, maxAge: 12, category: 'math' },
  { id: 'number-jumps', title: 'Number Jumps', icon: '🐸', color: 'from-emerald-400 to-emerald-500', minAge: 6, maxAge: 12, category: 'math' },
  { id: 'patterns', title: 'Pattern Quest', icon: '🔄', color: 'from-purple-400 to-purple-500', minAge: 6, maxAge: 12, category: 'logic' },
  { id: 'science', title: 'Science Lab', icon: '🔬', color: 'from-teal-400 to-teal-500', minAge: 6, maxAge: 12, category: 'science' },
  { id: 'memory', title: 'Memory Match', icon: '🧠', color: 'from-indigo-400 to-indigo-500', minAge: 7, maxAge: 12, category: 'logic' },
  { id: 'logic', title: 'Logic Puzzles', icon: '🧩', color: 'from-violet-400 to-violet-500', minAge: 7, maxAge: 12, category: 'logic' },
  { id: 'multiplication-jungle', title: 'Multiplication Jungle', icon: '🏴‍☠️', color: 'from-yellow-400 to-green-500', minAge: 7, maxAge: 12, category: 'math' },
  { id: 'multiplication-treasure-hunt', title: 'Treasure Hunt', icon: '💎', color: 'from-amber-400 to-orange-500', minAge: 7, maxAge: 12, category: 'math' },
  { id: 'multiplication-island', title: 'Island Blitz', icon: '⚡', color: 'from-cyan-400 to-blue-500', minAge: 7, maxAge: 12, category: 'math' },
  { id: 'multiplication-sudoku', title: 'Times Sudoku', icon: '🔢', color: 'from-green-500 to-teal-500', minAge: 8, maxAge: 12, category: 'math' },
  
  // SEL Games
  { id: 'sel-teddy-playground', title: 'Teddy\'s Playground', icon: '🧸', color: 'from-rose-400 to-pink-500', minAge: 4, maxAge: 5, category: 'sel' },
  { id: 'sel-feelings-faces', title: 'Feelings Faces', icon: '😊', color: 'from-yellow-400 to-orange-400', minAge: 4, maxAge: 6, category: 'sel' },
  { id: 'sel-bunny-choices', title: 'Bunny\'s Choices', icon: '🐰', color: 'from-purple-400 to-pink-400', minAge: 5, maxAge: 7, category: 'sel' },
  { id: 'sel-calm-bubbles', title: 'Calm-Down Bubbles', icon: '🫧', color: 'from-cyan-300 to-blue-400', minAge: 5, maxAge: 8, category: 'sel' },
];

const CATEGORY_LABELS = {
  math: { label: 'Math & Logic', icon: FiCpu, color: 'bg-green-100 text-green-700' },
  science: { label: 'Science', icon: FiZap, color: 'bg-blue-100 text-blue-700' },
  language: { label: 'Reading', icon: FiStar, color: 'bg-purple-100 text-purple-700' },
  logic: { label: 'Logic', icon: FiCpu, color: 'bg-indigo-100 text-indigo-700' },
  life: { label: 'Life Skills', icon: FiSmile, color: 'bg-teal-100 text-teal-700' },
  sel: { label: 'Social-Emotional', icon: FiHeart, color: 'bg-rose-100 text-rose-700' }
};

function GameDashboard() {
  const navigate = useNavigate();
  const { state } = useGame();
  const age = state.age || 7;
  const band = state.ageBand || getAgeBand(age);
  const earnedCount = getEarnedAchievements(state).length;

  // RULE: If Age 6, show all older games (maxAge >= 6).
  // RULE: If Age 8, hide anything under 8 (maxAge < 8).
  // Effectively: Filter for games where the maximum intended age is at least the current age.
  const games = ALL_GAMES.filter(g => g.maxAge >= age);

  const groupedGames = games.reduce((acc, game) => {
    const cat = game.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(game);
    return acc;
  }, {});

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen p-4 pt-24 pb-32">
      <div className="max-w-6xl mx-auto">
        {/* Welcome Header */}
        <motion.div initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mb-6">
          <div className={`rounded-3xl p-6 bg-gradient-to-r ${band.color} text-white shadow-xl`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-3xl font-kid mb-1">Hey, {state.playerName || 'Explorer'}! 🌟</h1>
                <p className="font-comic opacity-90">Ready for your next adventure?</p>
                <div className="inline-flex items-center gap-2 mt-2 bg-white/20 px-3 py-1 rounded-full text-sm font-comic">
                  {band.emoji} {band.label} Explorer · Age {age}
                </div>
              </div>
              <div className="flex gap-3">
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/quest-map')}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-2xl font-kid transition-all border border-white/30">
                  <SafeIcon icon={FiMap} /> Quest Map
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Row */}
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Level', value: state.level, emoji: '⭐' },
            { label: 'Score', value: state.score, emoji: '🎯' },
            { label: 'Tokens', value: state.tokens, emoji: '🪙' },
            { label: 'Achievements', value: `${earnedCount}`, emoji: '🏆' },
          ].map((stat, i) => (
            <motion.div key={i} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.15 + i * 0.05 }}
              className="bg-white rounded-2xl p-4 shadow-lg border-2 border-safari-200 text-center">
              <div className="text-2xl mb-1">{stat.emoji}</div>
              <div className="font-kid text-jungle-700 text-xl">{stat.value}</div>
              <div className="font-comic text-safari-600 text-xs">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Games by Category */}
        <div className="space-y-8">
          {Object.entries(groupedGames).map(([category, catGames], catIndex) => {
            const catInfo = CATEGORY_LABELS[category] || { label: category, color: 'bg-gray-100 text-gray-700' };
            return (
              <motion.div key={category}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 + catIndex * 0.1 }}>
                <div className="flex items-center gap-2 mb-4">
                  <div className={`p-2 rounded-xl ${catInfo.color}`}>
                    <SafeIcon icon={catInfo.icon} className="text-xl" />
                  </div>
                  <h2 className="font-kid text-jungle-700 text-2xl">{catInfo.label}</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {catGames.map((game, index) => {
                    const completed = state.completedQuests?.includes(game.id);
                    return (
                      <motion.div key={game.id}
                        initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.35 + index * 0.04 }}
                        whileHover={{ scale: 1.05, y: -4 }} whileTap={{ scale: 0.95 }}
                        onClick={() => navigate(`/game/${game.id}`)}
                        className={`bg-gradient-to-br ${game.color} rounded-3xl p-5 shadow-xl cursor-pointer text-white relative overflow-hidden`}>
                        {completed && (
                          <div className="absolute top-2 right-2 bg-white/30 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">✓</div>
                        )}
                        <div className="text-4xl mb-3">{game.icon}</div>
                        <h3 className="font-kid text-base leading-tight">{game.title}</h3>
                        <div className="absolute -bottom-3 -right-3 text-5xl opacity-10">{game.icon}</div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

export default GameDashboard;